<script setup lang="ts">
import type {PortfolioType} from "~/types/Portfolio";
import type {MapLocation} from "~/composables/useGoogleMap";
import {endpoints} from "~/constants/endpoints";
import {render} from "vue";
import { Button } from '~/components/ui/button'
import UserAvatar from "@/components/custom-ui/UserAvatar.vue";
import GoogleMap from "~/components/custom-ui/GoogleMap.vue";

const props = defineProps({
    specificationId: {
        type: String,
        required: true,
    },
    filters: {
        type: Object,
    }
})

const emit = defineEmits(['changeMode'])
const route = useRoute()
const mastersList = ref<PortfolioType[]>([])
const center = ref<MapLocation | null>(null)
const isMapVisible = computed(() => {
    return mastersMarkers.value.length > 0
})

const {
    getCurrentLocation,
} = useGoogleMap()

const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null)
const isMapReady = ref(false)
const bounds = ref<{ minLat: number; maxLat: number; minLng: number; maxLng: number; } | null>(null)
const pendingBounds = ref<{ minLat: number; maxLat: number; minLng: number; maxLng: number } | null>(null)

onMounted(async () => {
    try {
        const location = await getCurrentLocation()
        center.value = location
        bounds.value = useBoundsCalculator(location).value
        if (mapRef.value) {
            mapRef.value.setLocation(location)
        }
    } catch (error: any) {
        const defaultLocation: MapLocation = { lat: 25.057257, lng: 55.207695 }
        center.value = defaultLocation
        bounds.value = useBoundsCalculator(defaultLocation).value
        
        if (error?.code !== 3) {
            console.warn('Error getting location, using default coordinates:', error?.message || error)
        }
    }
})

async function getMasters() {
    if (!props.specificationId) return []
    await nextTick()
    let query = {
        ...props.filters,
        pageSize: 999999,
    }
    
    if(bounds.value){
        query = {
            ...query,
            ...(bounds.value as Record<string, any>)
        }
    }
    const response = await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
        method: 'GET',
        query: query
    }).then(({data, meta}) => {
        return data
    }).catch(e => {
        console.error(e)
    })
    mastersList.value = response?.filter(portfolio => !!portfolio.geo && !!portfolio.user) || []
}

watch(() => [props.filters, bounds], async ([filters, bounds]) => {
    if(bounds?.value){
        await getMasters()
    }
}, {immediate: true, deep: true})

const mastersMarkers = computed(() => {
    return mastersList.value
        .filter(item => item.geo && item.user)
        .map(item => {
            const markerContainer = document.createElement('div')
            markerContainer.className = 'relative w-11 h-11 rounded-full border-2 border-white shadow-md cursor-pointer flex items-center justify-center'
            
            const avatarUrl = item.user?.avatar?.url || (item.user?.avatar as any)?.formats?.small?.url
            
            render(
                h(UserAvatar, {
                    src: avatarUrl,
                    name: item.user.username,
                    size: 'xs',
                    onClick: () => {
                        navigateTo({
                            name: 'portfolio',
                            params: {id: item.documentId},
                            query: {mode: 'geo'}
                        })
                    }
                }),
                markerContainer
            )

            return {
                documentId: item.documentId,
                name: item.user.username,
                position: {
                    lat: item.geo!.lat,
                    lng: item.geo!.lng,
                },
                content: markerContainer
            }
        })
})

const applyFitBounds = (boundsToApply: { minLat: number; maxLat: number; minLng: number; maxLng: number }) => {
    if (!mapRef.value || !isMapReady.value) {
        pendingBounds.value = boundsToApply
        return
    }
    
    const map = mapRef.value.getMap()
    if (!map) {
        pendingBounds.value = boundsToApply
        return
    }
    
    nextTick(() => {
        mapRef.value?.fitBounds(boundsToApply, 50)
        pendingBounds.value = null
    })
}

const handleMapReady = () => {
    isMapReady.value = true
    if (pendingBounds.value) {
        setTimeout(() => {
            applyFitBounds(pendingBounds.value!)
        }, 100)
    }
}

watch(mapRef, async (value) => {
    if(value && center.value){
        await nextTick()
        mapRef.value?.setLocation(center.value)
    }
}, {immediate: true})

watch(bounds, (newBounds) => {
    if (newBounds) {
        applyFitBounds(newBounds)
    }
}, { immediate: true, deep: true })

</script>

<template>
    <div>
        <GoogleMap 
            v-show="isMapVisible"
            ref="mapRef"
            :center="center || {lat: 25.057257, lng: 55.207695}"
            min-height="200px"
            max-height="200px"
            :show-marker="true"
            :clickable="false"
            @ready="handleMapReady"
        >
            <template #markers>
                <ScriptGoogleMapsMarkerClusterer>
                    <ScriptGoogleMapsAdvancedMarkerElement
                            v-for="marker in mastersMarkers"
                            :key="marker.documentId"
                            :options="{ 
                                position: marker.position,
                                content: marker.content
                            }"
                        />
                </ScriptGoogleMapsMarkerClusterer>
            </template>
            <div class="absolute top-2 right-2 z-10">
                <Button
                    class="rounded-xl p-3 bg-white hover:bg-white z-10 space-x-1 text-gray-400 hover:text-primary-500 flex items-center shadow-md"
                    size="sm"
                    as-child
                >
                    <NuxtLink :to="{name: route.name, params: {slug: route.params.slug as string, city: route.params.city as string}, query: {mode: 'geo'}}" class="flex items-center gap-1">
                        <span class="transition-all">{{ $t('Show map') }}</span>
                        <Icon name="ri:map-2-line" size="24" class="transition-all" />
                    </NuxtLink>
                </Button>
            </div>
        </GoogleMap>
    </div>
</template>